/**
 * Utility for parsing RESTful wiki URLs
 * Handles URLs like: /wiki/armors/types/41/subtypes/2/details/123
 */

export interface WikiUrlParams {
  source: string;
  typeId?: number;
  subtypeId?: number;
  itemId?: number;
  // NPCs specific
  subcategoryId?: number;
  // Skills specific
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
  isDetailPage: boolean;
  isCategoryPage: boolean;
}

export const parseWikiUrl = (pathname: string): WikiUrlParams | null => {
  // Remove leading/trailing slashes and split
  const parts = pathname.replace(/^\/|\/$/g, '').split('/');

  // Must start with 'wiki'
  if (parts[0] !== 'wiki' || parts.length < 2) {
    return null;
  }

  const source = parts[1];
  let typeId: number | undefined;
  let subtypeId: number | undefined;
  let itemId: number | undefined;
  let subcategoryId: number | undefined;
  let raceId: number | undefined;
  let skillTypeId: number | undefined;
  let classId: number | undefined;
  let professionId: number | undefined;
  let isDetailPage = false;
  let isCategoryPage = false;

  // Parse RESTful structure
  for (let i = 2; i < parts.length; i += 2) {
    const key = parts[i];
    const value = parts[i + 1];

    if (!value) break;

    switch (key) {
      case 'types':
        if (source === 'skills') {
          // For skills: /wiki/skills/races/{id}/types/{id} → skillTypeId
          skillTypeId = parseInt(value);
        } else {
          // For armors/weapons: /wiki/armors/types/{id} → typeId
          typeId = parseInt(value);
        }
        break;
      case 'subtypes':
        subtypeId = parseInt(value);
        break;
      case 'details':
        itemId = parseInt(value);
        isDetailPage = true;
        break;
      // All sources: /wiki/{source}/subcategories/{id}
      case 'subcategories':
        subcategoryId = parseInt(value);
        break;
      // Skills structure: /wiki/skills/races/{id}/types/{id}/classes/{id}/professions/{id}
      case 'races':
        raceId = parseInt(value);
        break;
      case 'classes':
        classId = parseInt(value);
        break;
      case 'professions':
        professionId = parseInt(value);
        break;
      default:
        // Unknown key, might be category page
        break;
    }
  }

  // If no details found, it's a category page
  if (!isDetailPage) {
    isCategoryPage = true;
  }

  return {
    source,
    typeId,
    subtypeId,
    itemId,
    subcategoryId,
    raceId,
    skillTypeId,
    classId,
    professionId,
    isDetailPage,
    isCategoryPage
  };
};

export const buildWikiUrl = (params: {
  source: string;
  typeId?: number;
  subtypeId?: number;
  itemId?: number;
  subcategoryId?: number;
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
}): string => {
  let path = `/wiki/${params.source}`;

  if (params.source === 'skills') {
    // Skills hierarchy: /wiki/skills/races/{id}/types/{id}/classes/{id}/professions/{id}
    if (params.raceId) {
      path += `/races/${params.raceId}`;
      if (params.skillTypeId) {
        path += `/types/${params.skillTypeId}`;
        if (params.classId) {
          path += `/classes/${params.classId}`;
          if (params.professionId) {
            path += `/professions/${params.professionId}`;
          }
        }
      }
    }
  } else {
    // Standard hierarchy: /wiki/{source}/subcategories/{id}/types/{id}/subtypes/{id}
    // subcategoryId is used for ALL sources (npcs, weapons, armors, items, etc.)
    if (params.subcategoryId) {
      path += `/subcategories/${params.subcategoryId}`;
    }
    if (params.typeId) {
      path += `/types/${params.typeId}`;
      if (params.subtypeId) {
        path += `/subtypes/${params.subtypeId}`;
      }
    }
  }

  if (params.itemId) {
    path += `/details/${params.itemId}`;
  }

  return path;
};

/**
 * Check if current path is a wiki detail page
 */
export const isWikiDetailPage = (pathname: string): boolean => {
  const parsed = parseWikiUrl(pathname);
  return parsed?.isDetailPage || false;
};

/**
 * Check if current path is a wiki category page
 */
export const isWikiCategoryPage = (pathname: string): boolean => {
  const parsed = parseWikiUrl(pathname);
  return parsed?.isCategoryPage || false;
};